import { kc } from '../auth/keycloak.js';

export class ApiProblem extends Error {
  status: number;
  title: string;
  detail?: string;
  extensions: Record<string, unknown>;

  constructor(input: {
    status: number;
    title: string;
    detail?: string;
    extensions?: Record<string, unknown>;
  }) {
    super(input.title);
    this.name = 'ApiProblem';
    this.status = input.status;
    this.title = input.title;
    this.detail = input.detail;
    this.extensions = input.extensions ?? {};
  }
}

// One authenticated request: the bearer token from keycloak-js's memory, one
// refresh-and-retry on a 401, and an RFC 7807 problem thrown for anything not
// ok. Shared by the JSON reader below and the byte reader after it, so the two
// cannot drift on how a request is authorised.
async function apiResponse(
  path: string,
  init: RequestInit = {},
  opts: { retried?: boolean } = {},
): Promise<Response> {
  // Content-Type only when there is a body: Fastify rejects a JSON
  // content-type with an empty body (FST_ERR_CTP_EMPTY_JSON_BODY → 400)
  // before route logic runs, which silently broke every bodyless POST
  // (settlement-sync request, test-connection, intake promote).
  const headers: Record<string, string> = {
    ...(init.body != null ? { 'Content-Type': 'application/json' } : {}),
    Authorization: `Bearer ${kc.token ?? ''}`,
    ...((init.headers as Record<string, string>) ?? {}),
  };

  const res = await fetch(`/api/v1${path}`, { ...init, headers });

  if (res.status === 401 && !opts.retried) {
    try {
      await kc.updateToken(0);
    } catch {
      kc.logout();
    }
    return apiResponse(path, init, { retried: true });
  }

  if (!res.ok) {
    let problem: { title?: string; detail?: string } & Record<string, unknown> = {};
    try {
      problem = await res.json();
    } catch {
      // non-JSON body — fall through to status-text fallback
    }
    throw new ApiProblem({
      status: res.status,
      title: problem.title ?? res.statusText,
      detail: problem.detail,
      extensions: problem,
    });
  }
  return res;
}

export async function apiFetch<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const res = await apiResponse(path, init);
  if (res.status === 204) return null as T;
  return (await res.json()) as T;
}

/**
 * #767 — a binary response, read with the same bearer token as every other
 * call. This is how an image the API serves reaches the page: a bare
 * `<img src="/api/v1/...">` would be sent WITHOUT the token (it lives in JS
 * memory, never in a cookie or web storage) and 401, and a `blob:` URL is
 * outside the CSP's `img-src 'self' data:`. The caller turns the bytes into a
 * `data:` URL.
 */
export async function apiFetchBytes(path: string): Promise<{ bytes: ArrayBuffer; contentType: string }> {
  const res = await apiResponse(path);
  return { bytes: await res.arrayBuffer(), contentType: res.headers.get('Content-Type') ?? '' };
}
